import { Checkbox, cn, Input, Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@1trading/react';
import { useState } from 'react';
import { toast } from 'sonner';
import { type DayBounds, isWithinBounds } from '../date-bounds';
import { PROGRAM_TYPE_LABELS } from '../mm-program-editing';
import type { ProgramType } from '../mm-program-schema';
import { type FieldValue, type GridField, TYPE_OPTIONS } from './grid-fields';

const NUMBER_FORMAT = new Intl.NumberFormat('en-US', { maximumFractionDigits: 6 });

/** Shows `stored × scale`, trimming float noise such as 0.07 × 100. */
export const formatFieldValue = (field: GridField, value: FieldValue): string => {
  if (value === undefined || value === '') return field.key === 'end_date' ? 'Open' : '';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'number') return Number.isNaN(value) ? String(value) : NUMBER_FORMAT.format(Number((value * (field.scale ?? 1)).toPrecision(12)));
  if (field.kind === 'type') return PROGRAM_TYPE_LABELS[value as ProgramType] ?? value;
  return value;
};

/** What the editor starts from: digits without grouping, so the typed text parses back. */
const toEditText = (field: GridField, value: FieldValue) =>
  typeof value === 'number' ? (Number.isNaN(value) ? '' : String(Number((value * (field.scale ?? 1)).toPrecision(12)))) : typeof value === 'string' ? value : '';

/** Blank is undefined; anything else that is not a number is NaN, which the schema reports on the cell. */
export const parseFieldText = (field: GridField, text: string): FieldValue => {
  if (field.kind === 'number') return text.trim() ? Number(text.replaceAll(',', '')) / (field.scale ?? 1) : undefined;
  return text.trim();
};

export const TypeSelect = ({ value, onChange, disabled, className }: { value: string; onChange: (value: string) => void; disabled?: boolean; className?: string }) => (
  <Select disabled={disabled} onValueChange={onChange} value={value}>
    <SelectTrigger className={cn('h-7 text-xs', className)}>
      <SelectValue />
    </SelectTrigger>
    <SelectContent>
      {TYPE_OPTIONS.map((option) => (
        <SelectItem className="text-xs" key={option.value} value={option.value}>
          {option.label}
        </SelectItem>
      ))}
    </SelectContent>
  </Select>
);
TypeSelect.displayName = 'TypeSelect';

interface GridCellProps {
  field: GridField;
  value: FieldValue;
  applies: boolean;
  isReadOnly: boolean;
  isChanged: boolean;
  /** The loaded value, shown on hover of a changed cell */
  savedText?: string;
  problem?: string;
  /** Marker after the value, such as the NBBO widening on a bucket width */
  suffix?: string;
  /** The days a day field may be set to */
  bounds?: DayBounds;
  onCommit: (value: FieldValue) => void;
}

export const GridCell = ({ field, value, applies, isReadOnly, isChanged, savedText, problem, suffix, bounds, onCommit }: GridCellProps) => {
  const [editText, setEditText] = useState<string | null>(null);
  if (!applies) return <td aria-label={`${field.longLabel}: does not apply`} className="border-l border-border/40 bg-muted/30" />;

  const title = [problem, isChanged && savedText !== undefined ? `Was ${savedText || 'blank'}` : '', field.description].filter(Boolean).join('\n\n');
  const tone = cn(
    'border-l border-border/40 px-1.5 py-1 text-xs tabular-nums whitespace-nowrap',
    isChanged && 'bg-blue-500/10',
    problem && 'bg-red-500/10 outline outline-1 -outline-offset-1 outline-red-500',
    isReadOnly && 'text-muted-foreground',
  );
  const commit = (text: string) => {
    setEditText(null);
    const next = parseFieldText(field, text);
    if (text === toEditText(field, value)) return;
    if (field.kind === 'day' && typeof next === 'string' && next && bounds && !isWithinBounds(next, bounds)) {
      toast.error(`${next} cannot be used. ${bounds.reason ?? ''}`.trim());
      return;
    }
    onCommit(next);
  };

  if (field.kind === 'boolean') {
    return (
      <td className={cn(tone, 'text-center')} title={title}>
        <Checkbox aria-label={field.longLabel} checked={value === true} disabled={isReadOnly} onCheckedChange={(checked) => onCommit(checked === true)} />
      </td>
    );
  }
  if (field.kind === 'type') {
    return (
      <td className={tone} title={title}>
        {isReadOnly ? formatFieldValue(field, value) : <TypeSelect className="w-44" onChange={onCommit} value={String(value)} />}
      </td>
    );
  }
  if (editText !== null) {
    return (
      <td className={cn(tone, 'p-0.5')}>
        <Input
          aria-label={field.longLabel}
          autoFocus
          className="h-7 min-w-20 px-1.5 text-xs tabular-nums"
          inputMode={field.kind === 'number' ? 'decimal' : undefined}
          max={field.kind === 'day' ? bounds?.max : undefined}
          min={field.kind === 'day' ? bounds?.min : undefined}
          onBlur={(event) => commit(event.target.value)}
          onFocus={(event) => event.currentTarget.select()}
          onChange={(event) => setEditText(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') commit(event.currentTarget.value);
            if (event.key === 'Escape') setEditText(null);
          }}
          type={field.kind === 'day' ? 'date' : 'text'}
          value={editText}
        />
      </td>
    );
  }
  const shown = formatFieldValue(field, value);
  return (
    <td className={tone} title={title}>
      <span className={cn('flex items-center gap-1', field.kind === 'number' ? 'justify-end' : 'justify-start')}>
        {suffix ? <span className="shrink-0 rounded bg-violet-500/15 px-1 text-[10px] font-medium text-violet-600 dark:text-violet-300">{suffix}</span> : null}
        {isReadOnly ? (
          <span>{shown}</span>
        ) : (
          <button
            aria-label={`${field.longLabel}: ${shown || 'blank'}. Edit`}
            className={cn('min-w-10 flex-1 rounded-sm px-0.5 hover:bg-muted', field.kind === 'number' ? 'text-right' : 'text-left', !shown && 'text-muted-foreground')}
            onClick={() => setEditText(toEditText(field, value))}
            type="button"
          >
            {shown || '—'}
          </button>
        )}
      </span>
    </td>
  );
};
GridCell.displayName = 'GridCell';
